/**
 * 임대 계산식 프리셋 설정 — /settings/rental-preset (마이그 177 / 180).
 *
 * 시트 `임대 견적 계산기 v.0813` 의 두 표를 옮긴 화면이다. 상품군 열이 프리셋,
 * 정책 행이 원가 항목이며 **열도 행도 데이터**라 여기서 늘리고 줄이는 것만으로
 * 계산식이 바뀐다 — 배포가 필요 없다.
 *
 * ── 카드 구성이 시트와 다른 이유 ★ ──────────────────────────────────────
 *   시트의 '기본 정책' 표에는 원가 항목 8개와 정책 1개(초기 목표 마진율)가
 *   섞여 있고, '추가 정책' 표에는 정책 4개와 원가 항목 1개(신품 개봉 리스크율)가
 *   섞여 있다. 화면을 시트 표 그대로 자르면 저장 단위가 두 표를 가로지르게 된다
 *   (마진율은 프리셋 컬럼, 개봉 리스크율은 항목 행이라 API 가 다르다).
 *   그래서 **성질대로** 나눈다 — 가격 정책 카드(프리셋 컬럼) / 원가 항목 카드
 *   (rental_preset_cost_item). 카드 하나가 곧 엔드포인트 하나다.
 *
 * ── 계산 미리보기는 여기 없다 ──────────────────────────────────────────
 *   다음 단계다. 계산은 `@hcltech/shared` 의 `computeRentalPricing` 하나뿐이고
 *   브라우저에서 그대로 도니, 붙일 때도 서버 왕복 없이 이 화면 안에서 돈다.
 *
 * ── 입력 중 재렌더를 하지 않는다 ────────────────────────────────────────
 *   SettingsView 와 같은 원칙이다. 타이핑은 draft 만 갱신하고 DOM 을 건드리지
 *   않는다(커서 보호). 재렌더는 계산 방식(kind) 변경·행 추가/삭제·저장 완료처럼
 *   구조가 바뀔 때만 한다.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type {
  RentalPreset, RentalPresetItem, RentalPresetDefaultFor, RentalCostKind,
} from '../api/client';
import { RENTAL_COST_KINDS } from '@hcltech/shared/rental/rental-pricing';
import {
  computeRentalPricing, computeRentalPenalty,
  type RentalCostItem,
} from '@hcltech/shared/rental/rental-pricing';
import { getCurrentRole } from '../lib/auth';
import { escapeHtml } from '../lib/cells';

// ─── 어휘 ─────────────────────────────────────────────────────────────
const KIND_LABEL: Record<RentalCostKind, string> = {
  ANNUAL_RATE:   '연율 × 기간 (매입금액 × 율 × 개월 ÷ 12)',
  PURCHASE_RATE: '매입금액 비율 (매입금액 × 율)',
  FIXED:         '고정 금액 (1회)',
  FIXED_MONTHLY: '월 고정 금액 (금액 × 개월)',
};

/**
 * 기본 지정 표기 — **칩 배지와 경고 문구 전용**이다.
 *
 * ★ 화면에 기본 지정을 고르는 칸은 두지 않는다(2026-08-18 현민).
 *   신품 서버·중고 서버가 기본이라는 것은 정의상 고정이고, 그 밖의 프리셋은
 *   애초에 사람이 골라 쓰라고 만든 것이라 고를 자리가 필요 없다. 칸을 두면
 *   "여기서 뭘 골라야 하지?" 라는 질문만 만든다.
 *   옮길 일이 생기면 데이터 한 줄이다 — UPDATE rental_preset SET default_for=…
 */
const DEFAULT_FOR_LABEL: Record<RentalPresetDefaultFor, string> = {
  SERVER_NEW:  '신품 서버 라인',
  SERVER_USED: '중고 서버 라인',
};

/** 비율 단위인 kind — 화면에서 %로 보여주고 저장할 때 100으로 나눈다. */
function isRateKind(kind: RentalCostKind): boolean {
  return kind === 'ANNUAL_RATE' || kind === 'PURCHASE_RATE';
}

/**
 * 저장값 → 화면값.
 *
 * 비율은 %로 올린다(0.25 → 25). 부동소수 잔재(0.1*100 = 10.000000000000002)를
 * 없애려고 소수 4자리에서 끊는다 — DB 가 NUMERIC(_,4) 라 그 아래는 애초에
 * 저장되지 않는다.
 */
function toDisplay(value: number, kind: RentalCostKind): string {
  return isRateKind(kind) ? String(Math.round(value * 1e6) / 1e4) : String(value);
}
/** 화면값 → 저장값. 빈 칸·숫자가 아닌 입력은 0으로 본다(시트의 '-' 와 같다). */
function fromDisplay(text: string, kind: RentalCostKind): number {
  const n = Number(String(text).replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return 0;
  return isRateKind(kind) ? Math.round(n * 1e2) / 1e4 : n;
}
/** 정책 칸은 전부 비율(%)이라 kind 가 필요 없다. */
const pctToDisplay = (v: number): string => String(Math.round(v * 1e6) / 1e4);
const pctFromDisplay = (t: string): number => {
  const n = Number(String(t).replace(/,/g, '').trim());
  return Number.isFinite(n) ? Math.round(n * 1e2) / 1e4 : 0;
};
/**
 * 계산 결과 비율의 표시용 — 소수 둘째 자리까지, 불필요한 0 은 뗀다.
 *
 * `pctToDisplay` 와 나눈 이유: 저 함수는 **입력칸**용이라 사람이 적어 넣은 값을
 * 왜곡 없이 돌려주는 게 목적이다. 여기는 곡선이 만들어 낸 값(0.3111…)을 읽기
 * 좋게 자르는 자리라 반올림 폭이 다르다.
 */
const fmtRatePct = (v: number): string =>
  `${(Math.round(v * 1e4) / 1e2).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

// ─── 편집 상태 ────────────────────────────────────────────────────────
interface PolicyDraft {
  name:                 string;
  target_margin_rate:   string;   // %
  short_term_months:    string;   // 개월 — 할증 꼭짓점 T
  short_term_surcharge: string;   // % — 꼭짓점에서의 할증률 S
  normal_term_months:   string;   // 개월 — 정상 임대 L
  /**
   * %p — 옛 '꼭짓점 미만 1개월당 가산 U'. **계산에서 빠졌다 (2026-08-20).**
   * 할증 곡선이 '기준 개월 이하는 전부 같은 값' 으로 바뀌면서 쓰이지 않는다.
   * 화면에서 입력칸도 뺐고, 불러온 값이 그대로 다시 저장되기만 한다 —
   * DB 컬럼·견적 스냅샷(마이그 187/188)에 있어서 안 보내면 저장이 깨진다.
   */
  short_term_step_up:   string;
  penalty_rate:         string;   // %
  penalty_adjust:       string;   // %
}

interface ItemDraft {
  /** 렌더 키. 기존 항목은 id, 새 행은 'new-N'. */
  key:             string;
  id:              string | null;
  label:           string;
  kind:            RentalCostKind;
  value:           string;        // 표시 단위(% 또는 원)
}

/**
 * 계산 미리보기 입력.
 *
 * ★ 전부 문자열이다 — 입력 중인 값을 그대로 들고 있어야 커서가 안 튄다.
 *   숫자 변환은 계산 직전에 한 번만 한다(정책 칸과 같은 방식).
 */
interface PreviewDraft {
  /**
   * 매입단가 **개당**. 시트의 B(매입금액합계)는 여기에 수량을 곱해 만든다.
   *
   * ★ 예전에는 이 칸이 곧 B 였고 "수량을 이미 반영한 금액을 넣으세요" 라고
   *   안내했다. 옆에 수량 칸이 따로 있는데 매입금액에도 수량을 반영하라니
   *   수량을 두 번 신경 써야 했고, 수량만 2로 올리면 대당 단가가 절반으로
   *   떨어지는 이유를 화면에서 알 수 없었다. 견적 모달은 원래 개당 × 수량으로
   *   B 를 유도하므로, 미리보기도 같은 방식으로 맞춘다(서안드레 지적 260819).
   */
  unitPurchaseKrw: string;
  months:        string;   // 임대기간 N
  quantity:      string;   // 수량 Q
  manualUnitKrw: string;   // 수동 임대단가(시트 R열). 비우면 자동.
  remainMonths:  string;   // 중도해지 잔여개월 — 위약금 시뮬레이터용
}

interface Data {
  allowed:         boolean;
  loading:         boolean;
  loadError:       string;
  presets:         RentalPreset[];
  selectedId:      string | null;
  includeInactive: boolean;
  draft:           PolicyDraft | null;
  items:           ItemDraft[];
  newSeq:          number;
  policySaving:    boolean;
  policyMsg:       string;
  policyErr:       string;
  itemsSaving:     boolean;
  itemsMsg:        string;
  itemsErr:        string;
  preview:         PreviewDraft;
}

/**
 * '새 프리셋' 의 출발값 — 마이그 177 의 컬럼 DEFAULT 와 같다(시트의 표준 정책).
 * 복제는 원본 값에서 출발하므로 여기를 쓰지 않는다.
 */
const NEW_PRESET_DEFAULTS = {
  target_margin_rate:   0.30,
  short_term_months:    3,
  short_term_surcharge: 0.10,
  normal_term_months:   12,
  short_term_step_up:   0.10,
  penalty_rate:         0.50,
  penalty_adjust:       0,
} as const;

export class RentalPresetView extends SolaView<Data> {
  override events = {
    click: {
      '@select':      'handleSelect',
      '@inactive':    'handleToggleInactive',
      '@savepolicy':  'handleSavePolicy',
      '@saveitems':   'handleSaveItems',
      '@additem':     'handleAddItem',
      '@delitem':     'handleDelItem',
      '@newpreset':   'handleNewPreset',
      '@clonepreset': 'handleClonePreset',
      '@deactivate':  'handleDeactivate',
      '@reactivate':  'handleReactivate',
    },
    input:  { '@field': 'handleField', '@itemfield': 'handleItemField', '@preview': 'handlePreviewField' },
    change: { '@field': 'handleField', '@itemfield': 'handleItemField', '@preview': 'handlePreviewField' },
  };

  constructor() {
    super();
    // ★ 역할을 여기서 읽는다. onMount 에서 읽으면 안 된다 —
    //   라우터는 onMount 보다 **먼저** render() 를 한 번 부르므로, 초기값이
    //   false 면 화면에 "최고관리자·책임자만 볼 수 있습니다" 가 한 번 번쩍인다.
    //   getCurrentRole() 은 저장된 JWT 를 파싱하는 동기 함수라 생성자에서
    //   부르는 데 문제가 없다(비동기 하이드레이션이 아니다).
    const role = getCurrentRole();
    this.data = {
      allowed: role === 'owner' || role === 'leader',
      loading: true, loadError: '',
      presets: [], selectedId: null, includeInactive: false,
      draft: null, items: [], newSeq: 0,
      policySaving: false, policyMsg: '', policyErr: '',
      itemsSaving: false, itemsMsg: '', itemsErr: '',
      // 기본값 900만/12개월 — 신품 서버 시드로 시트의 6,440,000 이 그대로 나온다.
      preview: {
        unitPurchaseKrw: '9000000', months: '12', quantity: '1',
        manualUnitKrw: '', remainMonths: '0',
      },
    };
  }

  override getTitle(): string {
    return '임대 계산식 프리셋 — 설정 — HCLTech 어드민';
  }

  override onMount(): void {
    // 권한은 생성자에서 이미 정해졌다(첫 페인트가 틀리지 않도록).
    // 서버가 진짜 게이트이고 이건 노출용이다 — SettingsView 의 멤버 관리와 같다.
    if (!this.data.allowed) { this.data.loading = false; this.rerenderAll(); return; }
    // ★ .catch 를 붙인다. 예전에는 `void this.load()` 라 load() 가 reject 하면
    //   unhandled rejection 으로 사라지고 화면은 '불러오는 중…' 에 영원히 멈췄다.
    void this.load().catch((e) => {
      this.data.loading   = false;
      this.data.loadError = `화면 초기화 실패: ${(e as Error)?.message ?? e}`;
      this.rerenderAll();
    });
  }

  // ─── 로딩 ───────────────────────────────────────────────────────────
  private async load(keepId: string | null = null): Promise<void> {
    // ★ 이미 그려진 내용이 있으면 화면을 비우지 않는다.
    //   '불러오는 중…' 카드는 **보여줄 게 아무것도 없을 때**만 의미가 있다.
    //   저장·복제·비활성화 후 재조회에서도 깜빡이면 화면이 통째로 사라졌다가
    //   다시 나타나 눈이 튄다.
    if (this.data.presets.length === 0) {
      this.data.loading = true;
      this.rerenderAll();
    }
    try {
      const res = await api.listRentalPresets({ includeInactive: this.data.includeInactive });
      this.data.presets   = res.presets;
      this.data.loadError = '';
      const want = keepId ?? this.data.selectedId;
      const hit  = res.presets.find((p) => p.id === want) ?? res.presets[0] ?? null;
      this.select(hit);
    } catch (e) {
      // 응답 스키마 불일치(parseResponse)도 여기로 온다 — 404/500 과 구분이
      // 안 되면 원인을 못 좁히므로 메시지를 그대로 살린다.
      console.error('[RentalPreset] 목록 로드 실패', e);
      this.data.presets   = [];
      this.data.loadError = `프리셋 목록 로드 실패: ${(e as Error)?.message ?? e}`;
      this.data.draft     = null;
      this.data.items     = [];
    } finally {
      // ★ finally 다. 위에서 무엇이 터지든 '불러오는 중…' 으로 남지 않는다.
      this.data.loading = false;
      this.rerenderAll();
    }
  }

  /**
   * 선택 → 편집 상태 구성.
   *
   * 목록 응답은 활성 항목만 담고 있어 꺼 둔 항목이 보이지 않는다. 단건 조회는
   * 꺼진 것까지 주지만, 여기서는 **활성만** 편집 대상으로 둔다 — 꺼진 항목을
   * 표에 섞어 보여주면 "이 값이 계산에 들어가나?" 가 매번 헷갈린다.
   * 되살리기는 같은 이름으로 다시 추가하면 서버가 알아서 한다(id 불필요).
   */
  private select(p: RentalPreset | null): void {
    this.data.selectedId = p?.id ?? null;
    this.data.policyMsg = ''; this.data.policyErr = '';
    this.data.itemsMsg  = ''; this.data.itemsErr  = '';
    if (!p) { this.data.draft = null; this.data.items = []; return; }
    this.data.draft = {
      name:                 p.name,
      target_margin_rate:   pctToDisplay(p.target_margin_rate),
      short_term_months:    String(p.short_term_months),
      short_term_surcharge: pctToDisplay(p.short_term_surcharge),
      normal_term_months:   String(p.normal_term_months),
      short_term_step_up:   pctToDisplay(p.short_term_step_up),
      penalty_rate:         pctToDisplay(p.penalty_rate),
      penalty_adjust:       pctToDisplay(p.penalty_adjust),
    };
    this.data.items = p.items.filter((i) => i.is_active).map(toItemDraft);
  }

  private get selected(): RentalPreset | null {
    return this.data.presets.find((p) => p.id === this.data.selectedId) ?? null;
  }

  // ─── 입력 ───────────────────────────────────────────────────────────
  handleField(_e: Event, target: HTMLElement): void {
    const name = target.getAttribute('data-name') as keyof PolicyDraft | null;
    if (!name || !this.data.draft) return;
    this.data.draft[name] = (target as HTMLInputElement).value as never;
    this.clearPolicyFeedback();
    this.refreshPreview();
  }

  handleItemField(_e: Event, target: HTMLElement): void {
    const key   = target.getAttribute('data-key');
    const field = target.getAttribute('data-name');
    const row   = this.data.items.find((i) => i.key === key);
    if (!row || !field) return;
    const el = target as HTMLInputElement;
    if (field === 'label')       row.label = el.value;
    else if (field === 'value')  row.value = el.value;
    else if (field === 'kind') {
      // 단위가 바뀐다(% ↔ 원). 값 문자열은 그대로 두고 표만 다시 그려
      // 단위 라벨과 step 을 맞춘다 — 사람이 방금 친 숫자를 몰래 환산하지 않는다.
      row.kind = el.value as RentalCostKind;
      this.clearItemsFeedback();
      this.rerenderItems();
      this.refreshPreview();
      return;
    } else return;
    this.clearItemsFeedback();
    this.refreshPreview();
  }

  /**
   * '비활성 포함' 토글 — **칩 줄만** 갈아 끼운다.
   *
   * 목록을 다시 받아야 하지만 화면 전체를 다시 그릴 이유는 없다. 고른 프리셋이
   * 그대로 살아 있으면 가격 정책·원가 항목 카드는 건드리지 않는다 — 편집 중이던
   * 값이 날아가지 않게 하려는 것이 핵심이다.
   *
   * 선택이 사라지는 경우는 하나뿐이다: 비활성 프리셋을 고른 채로 체크를 끄는 것.
   * 그때만 첫 프리셋으로 옮기고 본문을 다시 그린다.
   */
  async handleToggleInactive(_e: Event, target: HTMLElement): Promise<void> {
    const on = (target as HTMLInputElement).checked;
    const prev = this.data.includeInactive;
    this.data.includeInactive = on;
    try {
      const res = await api.listRentalPresets({ includeInactive: on });
      this.data.presets = res.presets;
      if (res.presets.some((p) => p.id === this.data.selectedId)) {
        this.refreshRegion('.rp-tabs', () => this.renderTabsInner());
      } else {
        this.select(res.presets[0] ?? null);
        this.rerenderAll();
      }
    } catch (e) {
      console.error('[RentalPreset] 목록 재조회 실패', e);
      // 되돌린다 — 체크 상태와 데이터가 어긋난 채로 두면 다음 저장이 엉뚱해진다.
      this.data.includeInactive = prev;
      this.refreshRegion('.rp-tabs', () => this.renderTabsInner());
      this.setPolicyErr(`목록 재조회 실패: ${(e as Error)?.message ?? e}`);
    }
  }

  handleSelect(_e: Event, target: HTMLElement): void {
    const id = target.getAttribute('data-id');
    if (!id || id === this.data.selectedId) return;
    this.select(this.data.presets.find((p) => p.id === id) ?? null);
    this.rerenderAll();
  }

  handleAddItem(): void {
    this.data.newSeq += 1;
    this.data.items.push({
      key: `new-${this.data.newSeq}`, id: null, label: '',
      kind: 'PURCHASE_RATE', value: '0',
    });
    this.clearItemsFeedback();
    this.rerenderItems();
  }

  handleDelItem(_e: Event, target: HTMLElement): void {
    const key = target.getAttribute('data-key');
    this.data.items = this.data.items.filter((i) => i.key !== key);
    this.clearItemsFeedback();
    this.rerenderItems();
  }

  // ─── 저장 ───────────────────────────────────────────────────────────
  async handleSavePolicy(): Promise<void> {
    const d = this.data.draft, id = this.data.selectedId;
    if (!d || !id || this.data.policySaving) return;
    if (!d.name.trim()) { this.setPolicyErr('프리셋 이름을 입력하세요.'); return; }
    const margin = pctFromDisplay(d.target_margin_rate);
    // 단가 = 원가 ÷ (1 − 마진율) 이라 100% 는 0 나눗셈이다. 서버도 막지만
    // 여기서 먼저 말해 준다 — 저장 왕복 후 400 을 보는 것보다 낫다.
    if (margin >= 1) { this.setPolicyErr('목표 마진율은 100% 미만이어야 합니다.'); return; }

    // 꼭짓점이 정상 개월과 같거나 뒤면 감소 구간이 사라진다 — 4~11개월 할증이
    // 통째로 0 이 되어 곡선이 계단으로 되돌아간다. 서버(assertTermCurve)와 DB
    // CHECK 도 막지만, 저장 왕복 전에 어느 칸이 문제인지 말해 준다.
    const shortMonths  = Math.trunc(Number(d.short_term_months) || 0);
    const normalMonths = Math.trunc(Number(d.normal_term_months) || 0);
    if (normalMonths <= shortMonths) {
      this.setPolicyErr('정상 임대 개월은 할증 기준 개월보다 커야 합니다.');
      return;
    }

    this.setPolicySaving(true);
    try {
      // ★ default_for 는 보내지 않는다. API 계약상 **키가 없으면 유지**다
      //   (해제하려면 null 을 보내야 한다). 화면에 고르는 칸이 없으므로 안 건드린다.
      const updated = await api.updateRentalPreset(id, {
        name:                 d.name.trim(),
        target_margin_rate:   margin,
        short_term_months:    shortMonths,
        short_term_surcharge: pctFromDisplay(d.short_term_surcharge),
        normal_term_months:   normalMonths,
        short_term_step_up:   pctFromDisplay(d.short_term_step_up),
        penalty_rate:         pctFromDisplay(d.penalty_rate),
        penalty_adjust:       pctFromDisplay(d.penalty_adjust),
      });
      this.replacePreset(updated);
      this.data.policySaving = false;
      this.data.policyErr = '';
      this.data.policyMsg = '가격 정책을 저장했습니다.';
      this.rerenderAll();
    } catch (e) {
      this.setPolicySaving(false);
      this.setPolicyErr((e as Error).message || '저장 실패');
    }
  }

  async handleSaveItems(): Promise<void> {
    const id = this.data.selectedId;
    if (!id || this.data.itemsSaving) return;
    const rows = this.data.items;
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i]!.label.trim()) { this.setItemsErr(`${i + 1}번째 항목의 이름이 비어 있습니다.`); return; }
    }
    // 이름이 곧 식별자다(서버도 400 으로 막는다). 왕복 전에 잡아 어느 줄인지 말해 준다.
    const seen = new Set<string>();
    for (const r of rows) {
      const key = r.label.trim();
      if (seen.has(key)) { this.setItemsErr(`항목 이름 '${key}' 이(가) 중복됩니다.`); return; }
      seen.add(key);
    }

    this.setItemsSaving(true);
    try {
      const updated = await api.saveRentalPresetItems(id, rows.map((r) => ({
        id:    r.id,
        label: r.label.trim(),
        kind:  r.kind,
        value: fromDisplay(r.value, r.kind),
      })));
      this.replacePreset(updated);
      // 서버가 부여한 id·순서를 그대로 받는다 — 새 행이 계속 'new-N' 으로 남으면
      // 다음 저장 때 또 신규로 나가 같은 이름 행이 하나 더 생긴다.
      this.data.items = updated.items.filter((i) => i.is_active).map(toItemDraft);
      this.data.itemsSaving = false;
      this.data.itemsErr = '';
      this.data.itemsMsg = '원가 항목을 저장했습니다.';
      this.rerenderAll();
    } catch (e) {
      this.setItemsSaving(false);
      this.setItemsErr((e as Error).message || '저장 실패');
    }
  }

  async handleNewPreset(): Promise<void> {
    const name = window.prompt('새 프리셋 이름을 입력하세요.', '');
    if (name === null || !name.trim()) return;
    await this.createPreset(name.trim(), null);
  }

  /**
   * 복제 — 현재 프리셋의 **활성 항목**을 그대로 가진 새 프리셋.
   * 기본 지정은 물려받지 않는다(같은 자리를 둘이 가질 수 없다).
   */
  async handleClonePreset(): Promise<void> {
    const src = this.selected;
    if (!src) return;
    const name = window.prompt('복제본 이름을 입력하세요.', `${src.name} 사본`);
    if (name === null || !name.trim()) return;
    await this.createPreset(name.trim(), src);
  }

  private async createPreset(name: string, src: RentalPreset | null): Promise<void> {
    this.data.policyErr = '';
    try {
      const created = await api.createRentalPreset({
        name,
        default_for:          null,
        target_margin_rate:   src ? src.target_margin_rate   : NEW_PRESET_DEFAULTS.target_margin_rate,
        short_term_months:    src ? src.short_term_months    : NEW_PRESET_DEFAULTS.short_term_months,
        short_term_surcharge: src ? src.short_term_surcharge : NEW_PRESET_DEFAULTS.short_term_surcharge,
        normal_term_months:   src ? src.normal_term_months   : NEW_PRESET_DEFAULTS.normal_term_months,
        short_term_step_up:   src ? src.short_term_step_up   : NEW_PRESET_DEFAULTS.short_term_step_up,
        penalty_rate:         src ? src.penalty_rate         : NEW_PRESET_DEFAULTS.penalty_rate,
        penalty_adjust:       src ? src.penalty_adjust       : NEW_PRESET_DEFAULTS.penalty_adjust,
        sort_order:           this.data.presets.length + 1,
        copy_from_preset_id:  src ? src.id : null,
      });
      await this.load(created.id);
    } catch (e) {
      this.data.policyErr = (e as Error).message || '프리셋 생성 실패';
      this.rerenderAll();
    }
  }

  async handleDeactivate(): Promise<void> {
    const p = this.selected;
    if (!p) return;
    // 마지막 기본 프리셋을 끄면 그 종류의 라인이 프리셋을 자동 선택하지 못한다.
    // DB 에는 "최소 하나" 보장이 없으므로 여기서 말해 준다.
    const lastDefault = !!p.default_for
      && this.data.presets.filter((x) => x.is_active && x.default_for === p.default_for).length <= 1;
    const warn = lastDefault
      ? `\n\n⚠ 이 프리셋은 '${DEFAULT_FOR_LABEL[p.default_for!]}' 의 유일한 기본 프리셋입니다.`
        + ` 끄면 해당 라인이 프리셋을 자동으로 고르지 못합니다.`
      : '';
    if (!window.confirm(`'${p.name}' 프리셋을 비활성화합니다.\n`
      + `지워지지 않으며 '비활성 포함' 을 켜면 다시 보입니다.${warn}`)) return;
    try {
      await api.deactivateRentalPreset(p.id);
      await this.load(null);
    } catch (e) {
      this.data.policyErr = (e as Error).message || '비활성화 실패';
      this.rerenderAll();
    }
  }

  /**
   * 다시 활성화.
   *
   * 비활성화가 소프트 삭제라 되돌릴 길이 있어야 한다 — 없으면 실수로 끈 프리셋을
   * 화면에서 살릴 방법이 없어 DB 를 직접 만져야 했다.
   *
   * ★ 이름 충돌이 날 수 있다. 유니크 인덱스가 `WHERE is_active` 라 끄는 순간
   *   이름 자리가 풀리고, 그 사이 같은 이름으로 새 프리셋을 만들 수 있다.
   *   그때 되살리면 409 다 — 서버 메시지를 그대로 띄운다.
   */
  async handleReactivate(): Promise<void> {
    const p = this.selected;
    if (!p || p.is_active) return;
    try {
      const updated = await api.updateRentalPreset(p.id, { is_active: true });
      this.replacePreset(updated);
      this.select(updated);
      this.rerenderAll();
    } catch (e) {
      this.setPolicyErr((e as Error).message || '활성화 실패');
    }
  }

  // ─── 계산 미리보기 ──────────────────────────────────────────────────
  handlePreviewField(_e: Event, target: HTMLElement): void {
    const name = target.getAttribute('data-name') as keyof PreviewDraft | null;
    if (!name) return;
    const el = target as HTMLInputElement;
    this.data.preview[name] = el.value as never;
    this.refreshPreview();
  }

  /** 결과 영역만 다시 그린다 — 입력 칸을 건드리지 않아 커서가 안 튄다. */
  private refreshPreview(): void {
    this.refreshRegion('.rp-preview-out', () => this.renderPreviewOut());
  }

  private replacePreset(p: RentalPreset): void {
    const i = this.data.presets.findIndex((x) => x.id === p.id);
    if (i >= 0) this.data.presets[i] = p;
    else this.data.presets.push(p);
  }

  // ─── 부분 갱신 (커서 보호) ──────────────────────────────────────────
  /**
   * 본문 재렌더.
   *
   * ★ `this.render()` 를 두 번 부르면 안 된다 — 2026-08-18에 이걸로 화면이
   *   통째로 멎었다. Sola 의 `htmlSolaWithInstance(this)` 는 내부적으로
   *   `createElementFromHtml(..., this)` → `this.attach(새 element)` 를 부르고,
   *   `attach()` 는 `_rootElement` 를 **방금 만든(문서에 붙지 않은) element** 로
   *   갈아치운다. 그래서
   *
   *     const root = this._rootElement;            // 화면에 붙은 노드
   *     root.innerHTML = this.render().innerHTML;  // ← 여기서 _rootElement 가 고아가 됨
   *
   *   이 패턴은 **첫 호출만** 동작하고, 두 번째부터는 고아 노드에 쓴다.
   *   렌더는 계속 도는데 화면은 첫 페인트에 박제된다(parentElement === null).
   *
   *   코어가 이 함정 때문에 `refreshRegion` 을 제공한다 — 이건 render() 를
   *   부르지 않아 `_rootElement` 가 마운트된 노드 그대로 유지되고, 교체된
   *   subtree 의 [data-bind] 재등록까지 해 준다. 이벤트는 root 위임이라
   *   innerHTML 교체에 영향이 없다.
   */
  private rerenderAll(): void {
    try {
      this.refreshRegion('.rp-body', () => this.renderBody());
    } catch (e) {
      console.error('[RentalPreset] 렌더 실패', e);
      this.refreshRegion('.rp-body',
        `<div class="settings-card"><p class="text-danger">화면 렌더 실패: `
        + `${escapeHtml((e as Error)?.message ?? String(e))}</p></div>`);
    }
  }
  private rerenderItems(): void {
    this.refreshRegion('.rp-items-host', () => this.renderItemsTable());
  }
  private setPolicySaving(on: boolean): void {
    this.data.policySaving = on;
    const b = this._rootElement?.querySelector<HTMLButtonElement>('.rp-save-policy');
    if (b) { b.disabled = on; b.textContent = on ? '저장 중…' : '가격 정책 저장'; }
  }
  private setItemsSaving(on: boolean): void {
    this.data.itemsSaving = on;
    const b = this._rootElement?.querySelector<HTMLButtonElement>('.rp-save-items');
    if (b) { b.disabled = on; b.textContent = on ? '저장 중…' : '원가 항목 저장'; }
  }
  /** 메시지 한 칸만 갈아 끼운다 — 재렌더하면 편집 중인 입력이 날아간다. */
  private paint(sel: string, msg: string): void {
    const el = this._rootElement?.querySelector<HTMLElement>(sel);
    if (el) { el.textContent = msg; el.classList.toggle('hidden', !msg); }
  }
  private setPolicyErr(msg: string): void {
    this.data.policyErr = msg; this.paint('.rp-policy-err', msg);
  }
  private setItemsErr(msg: string): void {
    this.data.itemsErr = msg; this.paint('.rp-items-err', msg);
  }
  private clearPolicyFeedback(): void {
    if (this.data.policyErr) this.setPolicyErr('');
    if (this.data.policyMsg) { this.data.policyMsg = ''; this.paint('.rp-policy-msg', ''); }
  }
  private clearItemsFeedback(): void {
    if (this.data.itemsErr) this.setItemsErr('');
    if (this.data.itemsMsg) { this.data.itemsMsg = ''; this.paint('.rp-items-msg', ''); }
  }

  // ─── 렌더 ───────────────────────────────────────────────────────────
  private renderPresetTabs(): string {
    return `<div class="rp-tabs">${this.renderTabsInner()}</div>`;
  }

  /** 칩 줄의 내용물. `.rp-tabs` 만 따로 갈아 끼울 수 있게 껍데기와 나눈다. */
  private renderTabsInner(): string {
    const chips = this.data.presets.map((p) => {
      const on  = p.id === this.data.selectedId;
      const tag = p.default_for
        ? `<small class="rp-chip-tag" title="${escapeHtml(DEFAULT_FOR_LABEL[p.default_for])}에서 미리 골라집니다 (초기값 — 견적에서 바꿀 수 있습니다)">기본</small>`
        : '';
      const off = p.is_active ? '' : `<small class="rp-chip-off">꺼짐</small>`;
      return `<button class="btn btn-sm rp-chip ${on ? 'btn-primary' : 'btn-secondary'}"
                      type="button" data-action="select" data-id="${escapeHtml(p.id)}">
                ${escapeHtml(p.name)}${tag}${off}
              </button>`;
    }).join('');
    return `
        ${chips || `<span class="text-muted">프리셋이 없습니다.</span>`}
        <button class="btn btn-sm btn-secondary" type="button" data-action="newpreset">+ 새 프리셋</button>
        <label class="rp-inactive-toggle">
          <input type="checkbox" data-action="inactive" ${this.data.includeInactive ? 'checked' : ''}>
          <span>비활성 포함</span>
        </label>`;
  }

  private renderPolicyCard(): string {
    const d = this.data.draft;
    if (!d) return '';
    const p = this.selected;
    return `
      <div class="settings-card">
        <h2>가격 정책</h2>
        <p class="settings-card-desc">
          시트의 '초기 목표 마진율'(기본 정책)과 '단기 임대 기준·할증률·위약금률·요율 변경'(추가 정책)입니다.
          원가가 아니라 원가 <em>위에</em> 붙는 단계라 원가 항목과 따로 저장합니다.
        </p>
        <div class="rp-grid">
          <label class="settings-field">
            <span>프리셋 이름</span>
            <input class="input" type="text" maxlength="60"
                   data-action="field" data-name="name" value="${escapeHtml(d.name)}">
          </label>
          ${pctField('초기 목표 마진율', 'target_margin_rate', d.target_margin_rate,
                     '매출 대비입니다. 단가 = 원가 ÷ (1 − 마진율). 100% 미만이어야 합니다.')}
          <label class="settings-field">
            <span>할증 기준 <small class="rp-unit">개월</small></span>
            <input class="input" type="number" min="0" max="120" step="1"
                   data-action="field" data-name="short_term_months"
                   value="${escapeHtml(d.short_term_months)}">
            <small class="settings-hint">이 개월 <b>이하</b>는 전부 아래 할증률입니다. 이 개월을 넘으면 정상 임대까지 선형으로 줄어듭니다.</small>
          </label>
          ${pctField('할증 기준 할증률', 'short_term_surcharge', d.short_term_surcharge,
                     '원가가 아니라 가격 정책입니다 — 단가에 곱해집니다. 기준 개월 이하는 전부 이 값이고, 넘으면 정상 임대까지 선형으로 줄어듭니다.')}
          <label class="settings-field">
            <span>정상 임대 <small class="rp-unit">개월</small></span>
            <input class="input" type="number" min="1" max="120" step="1"
                   data-action="field" data-name="normal_term_months"
                   value="${escapeHtml(d.normal_term_months)}">
            <small class="settings-hint">이 개월부터 할증이 <b>0%</b>입니다. 기준 개월보다 커야 합니다.</small>
          </label>
          <!-- ★ '기준 미만 가산율'(short_term_step_up) 입력칸을 뺐다 (2026-08-20).
               할증 곡선이 '기준 개월 이하는 전부 같은 값' 으로 바뀌면서 이 값은
               계산에 전혀 쓰이지 않는다(shared/rental-pricing.ts 의
               rentalSurchargeRate 참조). 아무 일도 안 하는 칸을 남겨 두면
               사람이 값을 바꿔 놓고 화면이 안 움직인다며 다시 제보하게 된다.

               필드 자체는 draft·저장 payload 에 그대로 흐른다 — DB 컬럼과 견적
               스냅샷(마이그 187/188)에 이미 있어서, 안 보내면 저장이 깨진다.
               불러온 값이 그대로 다시 저장될 뿐이다. 되살릴 일이 생기면 이
               자리에 pctField 한 줄을 되돌리면 된다. -->
          ${pctField('기본 위약금률', 'penalty_rate', d.penalty_rate,
                     '잔여 개월당. 견적서에는 인쇄하지 않습니다.')}
          ${pctField('위약금 요율 변경', 'penalty_adjust', d.penalty_adjust,
                     '음수를 넣을 수 있습니다. 최종 요율 = MAX(0, 기본 위약금률 + 요율 변경).')}
        </div>
        <div class="rp-policy-err text-danger ${this.data.policyErr ? '' : 'hidden'}">${escapeHtml(this.data.policyErr)}</div>
        <div class="rp-policy-msg text-success ${this.data.policyMsg ? '' : 'hidden'}">${escapeHtml(this.data.policyMsg)}</div>
        <div class="rp-actions">
          <button class="btn btn-primary rp-save-policy" type="button" data-action="savepolicy"
                  ${this.data.policySaving ? 'disabled' : ''}>
            ${this.data.policySaving ? '저장 중…' : '가격 정책 저장'}
          </button>
          <button class="btn btn-secondary" type="button" data-action="clonepreset">이 프리셋 복제</button>
          ${p == null ? '' : p.is_active
            ? `<button class="btn btn-secondary rp-danger" type="button" data-action="deactivate">비활성화</button>`
            : `<button class="btn btn-secondary" type="button" data-action="reactivate">다시 활성화</button>`}
        </div>
      </div>`;
  }

  private renderItemsTable(): string {
    const rows = this.data.items.map((r, idx) => {
      const rate = isRateKind(r.kind);
      const kindOpts = RENTAL_COST_KINDS.map((k) =>
        `<option value="${k}" ${r.kind === k ? 'selected' : ''}>${escapeHtml(KIND_LABEL[k])}</option>`).join('');
      return `
        <tr>
          <td class="rp-col-no text-muted">${idx + 1}</td>
          <td><input class="input" type="text" maxlength="60" placeholder="예: 연 감가율"
                     data-action="itemfield" data-name="label" data-key="${escapeHtml(r.key)}"
                     value="${escapeHtml(r.label)}"></td>
          <td><select class="input" data-action="itemfield" data-name="kind"
                      data-key="${escapeHtml(r.key)}">${kindOpts}</select></td>
          <td class="rp-col-value">
            <input class="input rp-value" type="number" step="${rate ? '0.01' : '1'}"
                   data-action="itemfield" data-name="value" data-key="${escapeHtml(r.key)}"
                   value="${escapeHtml(r.value)}">
            <span class="rp-unit">${rate ? '%' : '원'}</span>
          </td>
          <td class="text-right">
            <button class="btn btn-sm btn-secondary" type="button"
                    data-action="delitem" data-key="${escapeHtml(r.key)}">삭제</button>
          </td>
        </tr>`;
    }).join('');
    return `
      <table class="settings-members-table rp-items-table">
        <thead>
          <tr>
            <th class="rp-col-no">#</th>
            <th>항목 이름</th>
            <th>계산 방식</th>
            <th class="rp-col-value">값</th>
            <th class="text-right">작업</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="5" class="text-muted">항목이 없습니다. 아래에서 추가하세요.</td></tr>`}</tbody>
      </table>`;
  }

  // ─── 계산 미리보기 렌더 ─────────────────────────────────────────────
  /**
   * 편집 중인 값으로 계산한다 — **저장된 값이 아니다.**
   *
   * 이 카드의 목적이 "저장하기 전에 이 마진율이 얼마짜리 단가를 만드는지 본다"
   * 라서, 위 두 카드의 draft 를 그대로 넣는다. 저장 버튼을 누르지 않아도
   * 숫자를 고치는 순간 결과가 따라 움직인다.
   *
   * 계산은 `computeRentalPricing` 하나뿐이고 브라우저에서 그대로 돈다 —
   * 서버에 미리보기 API 를 두면 같은 계산이 두 경로로 갈라진다.
   */
  private previewItems(): RentalCostItem[] {
    return this.data.items.map((r) => ({
      label: r.label.trim() || '(이름 없음)',
      kind:  r.kind,
      value: fromDisplay(r.value, r.kind),
    }));
  }

  private renderPreviewOut(): string {
    const d = this.data.draft;
    if (!d) return '';
    const p  = this.data.preview;
    const unitPurchase = numOf(p.unitPurchaseKrw);
    const months       = Math.trunc(numOf(p.months));
    const quantity     = Math.trunc(numOf(p.quantity));
    const manual       = numOf(p.manualUnitKrw);
    // 시트 A35 의 B — "서버 수량까지 이미 반영한 값". 사람이 곱하지 않고
    //   여기서 곱한다. 견적 모달의 rentalLinePurchaseKrw 와 같은 계산이다.
    const purchaseKrw  = unitPurchase * quantity;

    const policy = {
      targetMarginRate:   pctFromDisplay(d.target_margin_rate),
      shortTermMonths:    Math.trunc(numOf(d.short_term_months)),
      shortTermSurcharge: pctFromDisplay(d.short_term_surcharge),
      normalTermMonths:   Math.trunc(numOf(d.normal_term_months)),
      shortTermStepUp:    pctFromDisplay(d.short_term_step_up),
    };
    const items = this.previewItems();
    const ctx   = { purchaseKrw, months, quantity };
    const r = computeRentalPricing(items, ctx, policy, manual > 0 ? manual : null);

    const rows = r.costs.map((c) => `
      <tr>
        <td>${escapeHtml(c.label)}</td>
        <td class="text-muted">${escapeHtml(KIND_LABEL[c.kind])}</td>
        <td class="text-right">${won(c.amountKrw)}</td>
      </tr>`).join('');

    const marginPct = r.marginRatePct == null
      ? '<span class="text-muted">확인 필요</span>'
      : `${(Math.round(r.marginRatePct * 100) / 100).toLocaleString()}%`;

    const penaltyRate  = pctFromDisplay(d.penalty_rate);
    const penaltyAdj   = pctFromDisplay(d.penalty_adjust);
    const remainMonths = Math.trunc(numOf(p.remainMonths));
    const penalty = computeRentalPenalty(r.monthlyKrw, remainMonths, penaltyRate, penaltyAdj);
    const finalRate = Math.max(0, penaltyRate + penaltyAdj);

    const invalid = !(quantity > 0 && months > 0 && r.unitKrw > 0);

    return `
      ${invalid ? `<p class="text-danger rp-preview-warn">수량·기간·단가가 모두 0보다 커야 계약 금액이 나옵니다.</p>` : ''}

      <table class="settings-members-table rp-preview-table">
        <thead><tr><th>원가 항목</th><th>계산 방식</th><th class="text-right">금액</th></tr></thead>
        <tbody>
          <tr class="rp-preview-basis">
            <td colspan="2">매입금액 합계 <small class="text-muted">매입단가 × 수량 — 시트 B</small></td>
            <td class="text-right">${won(purchaseKrw)}</td>
          </tr>
          ${rows || `<tr><td colspan="3" class="text-muted">원가 항목이 없습니다.</td></tr>`}
          <tr class="rp-preview-sum">
            <td colspan="2"><b>임대총원가</b> <small class="text-muted">시트 O열</small></td>
            <td class="text-right"><b>${won(r.contractCostKrw)}</b></td>
          </tr>
        </tbody>
      </table>

      <table class="settings-members-table rp-preview-table">
        <tbody>
          <tr>
            <td>기본 초기단가 <small class="text-muted">원가 ÷ (1 − 마진율) ÷ 수량 ÷ 개월</small></td>
            <td class="text-right">${won(r.baseUnitKrw)}</td>
          </tr>
          <tr>
            <td>기간 할증
              ${r.surchargeApplied
                ? `<small class="rp-badge-on">${months}개월 → ${fmtRatePct(r.surchargeRate)}</small>`
                : `<small class="text-muted">미적용 — ${months}개월 ≥ 정상 임대 ${policy.normalTermMonths}개월</small>`}
            </td>
            <td class="text-right">${won(r.surchargedUnitKrw)}</td>
          </tr>
          <tr class="rp-preview-sum">
            <td><b>적용 임대단가</b>
              ${manual > 0 ? `<small class="rp-badge-on">수동 입력</small>` : ''}</td>
            <td class="text-right"><b>${won(r.unitKrw)}</b></td>
          </tr>
          <tr><td>월 임대금액 <small class="text-muted">단가 × 수량</small></td>
              <td class="text-right">${won(r.monthlyKrw)}</td></tr>
          <tr class="rp-preview-sum">
            <td><b>계약 총액</b> <small class="text-muted">월 금액 × ${months}개월</small></td>
            <td class="text-right"><b>${won(r.totalKrw)}</b></td>
          </tr>
          <tr><td>마진 금액</td><td class="text-right">${won(r.marginKrw)}</td></tr>
          <tr><td>실제 마진율</td><td class="text-right">${marginPct}</td></tr>
        </tbody>
      </table>

      <table class="settings-members-table rp-preview-table">
        <tbody>
          <tr class="rp-preview-sum">
            <td><b>중도해지 위약금</b>
              <small class="text-muted">월 금액 × 잔여 ${remainMonths}개월 ×
                MAX(0, ${(penaltyRate * 100).toLocaleString()}% + ${(penaltyAdj * 100).toLocaleString()}%)
                = ${(Math.round(finalRate * 10000) / 100).toLocaleString()}%</small>
            </td>
            <td class="text-right"><b>${won(penalty)}</b></td>
          </tr>
        </tbody>
      </table>
      <p class="settings-hint">위약금은 화면 확인용입니다 — 견적서에 인쇄하지 않습니다(견적 시점에는 잔여개월이 없습니다).</p>`;
  }

  private renderPreviewCard(): string {
    if (!this.data.draft) return '';
    const p = this.data.preview;
    return `
      <div class="settings-card">
        <h2>계산 미리보기</h2>
        <p class="settings-card-desc">
          <b>위에서 편집 중인 값</b>으로 계산합니다 — 저장하지 않아도 숫자를 고치면 결과가 바로 따라옵니다.
          견적에서 쓰는 계산 함수를 그대로 부르므로 실제 견적과 같은 값이 나옵니다.
        </p>
        <div class="rp-grid">
          <label class="settings-field">
            <span>매입단가 <small class="rp-unit">원 / 개</small></span>
            <input class="input" type="number" step="1" data-action="preview" data-name="unitPurchaseKrw"
                   value="${escapeHtml(p.unitPurchaseKrw)}">
            <small class="settings-hint"><b>개당</b> 매입원가입니다. 수량은 옆 칸에서 곱합니다.</small>
          </label>
          <label class="settings-field">
            <span>임대 기간 <small class="rp-unit">개월</small></span>
            <input class="input" type="number" step="1" min="0" data-action="preview" data-name="months"
                   value="${escapeHtml(p.months)}">
          </label>
          <label class="settings-field">
            <span>수량</span>
            <input class="input" type="number" step="1" min="0" data-action="preview" data-name="quantity"
                   value="${escapeHtml(p.quantity)}">
          </label>
          <label class="settings-field">
            <span>수동 임대단가 <small class="rp-unit">원</small></span>
            <input class="input" type="number" step="1" data-action="preview" data-name="manualUnitKrw"
                   placeholder="비우면 자동 계산" value="${escapeHtml(p.manualUnitKrw)}">
            <small class="settings-hint">넣으면 계산된 단가 대신 이 값이 적용됩니다(시트 R열).</small>
          </label>
          <label class="settings-field">
            <span>중도해지 잔여 <small class="rp-unit">개월</small></span>
            <input class="input" type="number" step="1" min="0" data-action="preview" data-name="remainMonths"
                   value="${escapeHtml(p.remainMonths)}">
          </label>
        </div>
        <div class="rp-preview-out">${this.renderPreviewOut()}</div>
      </div>`;
  }

  private renderItemsCard(): string {
    if (!this.data.draft) return '';
    return `
      <div class="settings-card">
        <h2>원가 항목</h2>
        <p class="settings-card-desc">
          임대총원가를 이루는 항목입니다. 항목끼리 서로 참조하지 않으므로 순서는 표시 순서일 뿐입니다.
          <b>표에서 뺀 항목은 지워지지 않고 꺼집니다</b> — 같은 이름으로 다시 추가하면 되살아납니다.
          <b>이 프리셋을 쓰는 라인에는 아래 항목이 모두 들어갑니다.</b> 항목을 빼야 하는 라인이 있으면
          그 라인용 프리셋을 따로 만들어 쓰세요 — 예를 들어 개별 파트 프리셋에는 물류비·인건비를 넣지 않습니다.
          <br>시트 대조: 여기는 <b>'기본 정책'(6~13행) 순서</b>입니다. 단 '신품 개봉 리스크율'은 시트에서
          추가 정책(20행)이지만 임대총원가에 더해지는 <b>원가</b>라 맨 뒤에 함께 둡니다
          — 반대로 '초기 목표 마진율'은 시트에서 기본 정책이지만 원가가 아니라 가격이라 위 카드에 있습니다.
        </p>
        <div class="rp-items-host">${this.renderItemsTable()}</div>
        <div class="rp-items-err text-danger ${this.data.itemsErr ? '' : 'hidden'}">${escapeHtml(this.data.itemsErr)}</div>
        <div class="rp-items-msg text-success ${this.data.itemsMsg ? '' : 'hidden'}">${escapeHtml(this.data.itemsMsg)}</div>
        <div class="rp-actions">
          <button class="btn btn-secondary" type="button" data-action="additem">+ 항목 추가</button>
          <button class="btn btn-primary rp-save-items" type="button" data-action="saveitems"
                  ${this.data.itemsSaving ? 'disabled' : ''}>
            ${this.data.itemsSaving ? '저장 중…' : '원가 항목 저장'}
          </button>
        </div>
      </div>`;
  }

  /**
   * 본문만 문자열로. `render()` 와 분리해 둔 이유는 위 `rerenderAll` 주석 참조 —
   * 갱신은 이 함수의 결과만 `.rp-body` 에 갈아 끼운다. 껍데기(header)는 한 번
   * 그린 뒤 건드리지 않는다.
   */
  private renderBody(): string {
    const d = this.data;
    if (!d.allowed) {
      return `<div class="settings-card"><p class="text-muted">
                임대 계산식 프리셋은 최고관리자·책임자만 볼 수 있습니다.
              </p></div>`;
    }
    if (d.loading) {
      return `<div class="settings-card"><p class="text-muted">불러오는 중…</p></div>`;
    }
    if (d.loadError) {
      return `<div class="settings-card"><p class="text-danger">${escapeHtml(d.loadError)}</p></div>`;
    }
    return `${this.renderPresetTabs()}${this.renderPolicyCard()}${this.renderItemsCard()}${this.renderPreviewCard()}`;
  }

  /** ★ 프레임워크(마운트)만 부른다. 앱 코드에서 직접 부르지 말 것. */
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-settings page-rental-preset">
        <header class="page-header">
          <h1>임대 계산식 프리셋</h1>
          <p class="page-header-desc">
            상품군마다 임대 단가를 어떻게 뽑을지 정합니다. 여기 값이 곧 계산식이라 코드 배포 없이 바뀝니다.
          </p>
        </header>
        <section class="page-body rp-body">${this.renderBody()}</section>
      </div>
    `;
  }
}

// ─── 파일 로컬 헬퍼 ───────────────────────────────────────────────────
function toItemDraft(i: RentalPresetItem): ItemDraft {
  return {
    key: i.id, id: i.id, label: i.label, kind: i.kind,
    value: toDisplay(i.value, i.kind),
  };
}

/** 미리보기 입력용 숫자 파싱. 빈 칸·이상한 입력은 0 (계산이 멎지 않게). */
function numOf(text: string): number {
  const n = Number(String(text).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

/** 원 표기 — 천 단위 구분. 계산은 이미 원 단위 정수다. */
function won(v: number): string {
  return `${Math.round(v).toLocaleString()}원`;
}

function pctField(label: string, name: string, value: string, hint: string): string {
  return `
    <label class="settings-field">
      <span>${escapeHtml(label)} <small class="rp-unit">%</small></span>
      <input class="input" type="number" step="0.01"
             data-action="field" data-name="${name}" value="${escapeHtml(value)}">
      <small class="settings-hint">${hint}</small>
    </label>`;
}
